import { useFormik } from "formik";
import React, { useEffect } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { FormElements } from "../../../shared/ui/forms/FormElements";
import HeadingH4 from "../../../shared/ui/headings/headingH4/HeadingH4";
import Icon from "../../../shared/ui/icon/Icon";
import {
    formStructureEditPatientRegistryRow,
    formStructureEditPatientRegistryRowDomicile,
    formStructureEditPatientRegistryRowResidence
} from "./patientRegistryData";

import SecondaryButton from "../../../shared/ui/buttons/secondary-button/SecondaryButton";
import {
    createPatient,
    updatePatient
} from "../../../../redux/order/orderCrud";
import { useDispatch, useSelector } from "react-redux";
import { selectSelectedCustomer } from "../../../../redux/customer/customerSlice";
import * as routeName from "../../../routes/routeNameAuthType";
import { GetPatientAction } from "../../../../redux/patient/patientActions";
import { useRoute } from "../../../utils/useRoute";
import { setErrorTypeAction } from "../../../../redux/errors/errorsActions";
import { validationsPatient } from "./data/validations";
import BtnPrimary from "../../../../shared/bootstrap/button/primary/BtnPrimary";
import BtnSecondary from "../../../../shared/bootstrap/button/secondary/BtnSecondary";
import { useLanguageForShared } from "../../../../shared/utils/languages/useLanguageForShared";

// Helper to check if any residence/domicile field differs
function isDomicileDifferent(residence, domicile) {
    if (!residence || !domicile) return false;
    return Object.keys(residence).some(
        key => residence[key] !== domicile[key]
    );
}

const PatientRegistry = ({ patient = {}, closeModalHandler }) => {
    const { t } = useLanguageForShared();

    const customer = useSelector(selectSelectedCustomer);

    const dispatch = useDispatch();

    const { changeRoute } = useRoute();

    const shouldCopyDomicile = React.useRef(false);

    // Compute initial value for the checkbox
    const initialIsDomicileDifferent = isDomicileDifferent(
        patient.residence,
        patient.domicile
    );

    const formik = useFormik({
        initialValues: {
            ...patient,
            isDomicileDifferent: initialIsDomicileDifferent,
        },
        validationSchema: validationsPatient,
        onSubmit: (values) => {
            if (patient?.id) {
                updatePatient(values)
                    .then((res) => {
                        //const callback = () => changeRoute(routeName.selectedPatientPage);
                        dispatch(GetPatientAction(t, res.id, /*callback*/));
                    })
                    .catch((err) => {
                        const message = err?.response?.data?.Message;
                        if (message === "ERR_CF_DUPLICATE") {
                            dispatch(setErrorTypeAction(t('sg.msg.cfAlreadyRegistered')))
                        } else if (message === "ERR_CF_INCOHERENT") {
                            dispatch(setErrorTypeAction(t('sg.msg.cfWrong')))
                        } else if (message === "ERR_EMAIL_DUPLICATE") {
                            dispatch(setErrorTypeAction(t('sg.msg.emailAlreadyRegistered')))
                        } else if (message === "ERR_EMAIL_INCOHERENT") {
                            dispatch(setErrorTypeAction(t('sg.msg.emailWrong')))
                        } else {
                            dispatch(
                                dispatch(setErrorTypeAction(t('sg.msg.errorUpdatePatient')))
                            );
                        }
                    });
            } else {
                createPatient(values, customer.id)
                    .then((res) => {
                        const callback = () => changeRoute(routeName.products);
                        dispatch(GetPatientAction(t, res.id, callback));
                    })
                    .catch((err) => {
                        const message = err?.response?.data?.Message;
                        if (message === "ERR_CF_DUPLICATE") {
                            dispatch(setErrorTypeAction(t('sg.msg.cfAlreadyRegistered')))
                        } else if (message === "ERR_CF_INCOHERENT") {
                            dispatch(setErrorTypeAction(t('sg.msg.cfWrong')))
                        } else if (message === "ERR_EMAIL_DUPLICATE") {
                            dispatch(setErrorTypeAction(t('sg.msg.emailAlreadyRegistered')))
                        } else if (message === "ERR_EMAIL_INCOHERENT") {
                            dispatch(setErrorTypeAction(t('sg.msg.emailWrong')))
                        } else {
                            dispatch(
                                setErrorTypeAction(t('sg.msg.errorInsertPatient'))
                            );
                        }
                    });
            }
            closeModalHandler();
        }
    });

    // Sincronizza solo il campo 'street'
    useEffect(() => {
        if (
            formik.values.isDomicileDifferent === false &&
            formik.values.residence?.street !== undefined &&
            formik.values.domicile
        ) {
            const val = formik.values.residence.street;
            if (formik.values.domicile.street !== val) {
                formik.setFieldValue("domicile.street", val, false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.residence?.street]);

    // Sincronizza solo il campo 'number'
    useEffect(() => {
        if (
            formik.values.isDomicileDifferent === false &&
            formik.values.residence?.number !== undefined &&
            formik.values.domicile
        ) {
            const val = formik.values.residence.number;
            if (formik.values.domicile.number !== val) {
                formik.setFieldValue("domicile.number", val, false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.residence?.number]);

    // Sincronizza solo il campo 'internal'
    useEffect(() => {
        if (
            formik.values.isDomicileDifferent === false &&
            formik.values.residence?.internal !== undefined &&
            formik.values.domicile
        ) {
            const val = formik.values.residence.internal;
            if (formik.values.domicile.internal !== val) {
                formik.setFieldValue("domicile.internal", val, false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.residence?.internal]);

    // Sincronizza solo il campo 'cap'
    useEffect(() => {
        if (
            formik.values.isDomicileDifferent === false &&
            formik.values.residence?.cap !== undefined &&
            formik.values.domicile
        ) {
            const val = formik.values.residence.cap;
            if (formik.values.domicile.cap !== val) {
                formik.setFieldValue("domicile.cap", val, false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.residence?.cap]);

    // Sincronizza solo il campo 'city'
    useEffect(() => {
        if (
            formik.values.isDomicileDifferent === false &&
            formik.values.residence?.city !== undefined &&
            formik.values.domicile
        ) {
            const val = formik.values.residence.city;
            if (formik.values.domicile.city !== val) {
                formik.setFieldValue("domicile.city", val, false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.residence?.city]);

    // Sincronizza solo il campo 'province'
    useEffect(() => {
        if (
            formik.values.isDomicileDifferent === false &&
            formik.values.residence?.province !== undefined &&
            formik.values.domicile
        ) {
            const val = formik.values.residence.province;
            if (formik.values.domicile.province !== val) {
                formik.setFieldValue("domicile.province", val, false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.residence?.province]);

    useEffect(() => {
        if (
            shouldCopyDomicile.current &&
            formik.values.isDomicileDifferent === false
        ) {
            const residence = formik.values.residence;
            const domicile = formik.values.domicile;
            if (residence && domicile) {
                Object.keys(residence).forEach((key) => {
                    const residenceValue = residence[key];
                    if (
                        residenceValue !== undefined &&
                        residenceValue !== null &&
                        residenceValue !== "" &&
                        domicile[key] !== residenceValue
                    ) {
                        formik.setFieldValue(`domicile.${key}`, residenceValue, false);
                    }
                });
            }
            shouldCopyDomicile.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.isDomicileDifferent]);



    return (
        <div>
            <div className="d-flex">
                <p
                    className="sub-title mb-4">{patient?.id ? t('sg.editPatientRegistry') : t('sg.addPatientRegistry')}</p>
            </div>
            <Form onSubmit={formik.handleSubmit}>
                <FormElements
                    structure={formStructureEditPatientRegistryRow(t)}
                    formik={formik}
                ></FormElements>

                <FormElements
                    structure={formStructureEditPatientRegistryRowResidence(t)}
                    formik={formik}
                ></FormElements>

                {/* Checkbox between residence and domicile */}
                <div className="mb-3" id="isDomicileDifferent">
                    <Form.Check
                        type="checkbox"
                        label={t('sg.patient.differentDomicile')}
                        name="isDomicileDifferent"
                        checked={formik.values.isDomicileDifferent}
                        onChange={e => {
                            const checked = e.target.checked;
                            if (!checked) {
                                shouldCopyDomicile.current = true;
                            }
                            formik.setFieldValue("isDomicileDifferent", checked);
                        }}
                    />
                </div>

                <FormElements
                    structure={formStructureEditPatientRegistryRowDomicile(t)}
                    formik={formik}
                    disabled={!formik.values.isDomicileDifferent}
                />

                <Row className="justify-content-end mt-5">
                    <Col xs="auto">
                        <BtnSecondary
                            onClick={() => closeModalHandler()/*changeRoute(routeName.selectedPatientPage)*/}
                        >
                            {t('action.cancel')}
                        </BtnSecondary>
                    </Col>
                    <Col xs="auto">
                        <BtnPrimary
                            type={"submit"}
                            data-test={
                                patient?.id ? "edit-patient-test" : "create-patient-test"
                            }
                        >
                            {patient?.id ? t('action.edit')
                                : t('sg.form.action.addNewService')}
                        </BtnPrimary>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default PatientRegistry;
